import React from "react";
import { 
  Activity, Hammer, ShoppingBag, Utensils, Factory, Building2, Laptop, Car, 
  ArrowRight, Sparkles, Search, Compass, MapPin
} from "lucide-react";
import { getAllIndustries, IndustryRecord } from "../../utils/industryRegistry";
import { useSeoMetadata } from "../PublicPages";
import { SeoBreadcrumbs } from "./SeoBreadcrumbs";
import { SeoRelatedLinks } from "./SeoRelatedLinks";

interface IndustryDirectoryPageProps {
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
  currentUser?: any;
}

export function IndustryDirectoryPage({ onNavigate, onLogin = () => {}, currentUser }: IndustryDirectoryPageProps) {
  const industries = getAllIndustries();

  useSeoMetadata({
    title: "Browse Local Employers by Industry | BizNearYou",
    description: "Explore independent local employers, outpatient clinics, trade contractors, specialty merchants, and law practices near you.",
    canonicalUrl: "https://getbiznearyou.com/industries",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Local Employers by Industry Directory",
      "itemListElement": industries.map((ind, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "name": ind.name,
        "url": `https://getbiznearyou.com/industries/${ind.slug}`
      }))
    }
  });

  const getIcon = (name: string) => {
    switch (name) {
      case "Activity": return <Activity className="w-5 h-5 text-emerald-600" />;
      case "Hammer": return <Hammer className="w-5 h-5 text-amber-600" />;
      case "ShoppingBag": return <ShoppingBag className="w-5 h-5 text-purple-600" />;
      case "Utensils": return <Utensils className="w-5 h-5 text-rose-600" />;
      case "Factory": return <Factory className="w-5 h-5 text-blue-600" />;
      case "Building2": return <Building2 className="w-5 h-5 text-indigo-600" />;
      case "Laptop": return <Laptop className="w-5 h-5 text-cyan-600" />;
      case "Car": return <Car className="w-5 h-5 text-orange-600" />;
      default: return <Compass className="w-5 h-5 text-slate-600" />;
    }
  };

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    onNavigate(path);
  };

  return (
    <div className="w-full">
      <main className="flex-grow max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SeoBreadcrumbs items={[{ label: "Industries", path: "/industries" }]} onNavigate={onNavigate} />

        {/* Hero Header */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 mb-10 shadow-xs relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative z-10 max-w-3xl space-y-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-cyan-50 dark:bg-cyan-950/80 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Local Employer Industry Intelligence</span>
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-950 dark:text-white tracking-tight leading-tight">
              Explore Local Employers by Industry
            </h1>
            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              Discover real, physically operating small businesses, outpatient clinics, trade contractors, and professional firms in your community. Uncover unposted job opportunities and connect directly with local managing partners.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <a
                href="/find-jobs"
                onClick={(e) => handleLinkClick(e, "/find-jobs")}
                className="px-5 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-sm transition shadow-xs flex items-center gap-2 cursor-pointer"
              >
                <Search className="w-4 h-4" />
                <span>Search Local Businesses</span>
              </a>
              <a
                href="/locations"
                onClick={(e) => handleLinkClick(e, "/locations")}
                className="px-5 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold text-sm transition flex items-center gap-2 cursor-pointer"
              >
                <MapPin className="w-4 h-4" />
                <span>Browse Cities</span>
              </a>
            </div>
          </div>
        </div>

        {/* Industry Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {industries.map((ind) => (
            <div
              key={ind.slug}
              className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-600 transition flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 rounded-xl bg-slate-100 border border-slate-200">
                    {getIcon(ind.iconName)}
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">
                      <a
                        href={`/industries/${ind.slug}`}
                        onClick={(e) => handleLinkClick(e, `/industries/${ind.slug}`)}
                        className="hover:text-indigo-600 transition cursor-pointer"
                      >
                        {ind.name}
                      </a>
                    </h2>
                    <span className="text-[11px] font-medium text-slate-500">
                      Density: {ind.estimatedBusinessDensity}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-slate-600 leading-relaxed mb-4">
                  {ind.shortDescription}
                </p>

                <div className="mb-4">
                  <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1.5">
                    Primary Employers:
                  </span>
                  <ul className="text-xs text-slate-600 space-y-1">
                    {ind.primaryEmployers.slice(0, 3).map((emp, i) => (
                      <li key={i} className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />
                        <span className="truncate">{emp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                <a
                  href={`/industries/${ind.slug}`}
                  onClick={(e) => handleLinkClick(e, `/industries/${ind.slug}`)}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 cursor-pointer"
                >
                  <span>Explore Industry Hub</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          ))}
        </div>

        <SeoRelatedLinks currentRoute="/industries" onNavigate={onNavigate} />
      </main>
    </div>
  );
}
